import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/atoms/Icon';
import Logo from '../components/atoms/Logo';

const Login = () => {
  const { login, isAuthenticated, loading, error, isCodeSent } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, setFocus } = useForm();

  useEffect(() => { setFocus('nit'); }, [setFocus]);
  useEffect(() => { if (isCodeSent) navigate('/verify', { replace: true }); }, [isCodeSent, navigate]);

  const onSubmit = async (data) => { await login(data.nit); };

  const handleDownloadInstructivo = (e) => {
    setDownloading(true);

    const link = document.createElement('a');
    link.href = '/resources/docs/instructivo.pdf';
    link.download = 'instructivo-pse-eccargo.pdf';
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setDownloading(false), 1000);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center hero-login">
      {isAuthenticated && <Navigate to="/dashboard" replace />}
      <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px] pointer-events-none" />

      <div className="relative w-full max-w-2xl px-6">
        <div className="flex flex-col items-center">
          <Logo size="2xl" />

          <div className="mt-6 items-center gap-3 x-5 py-2">
            <Logo.Pagos2 size="xl" />
            {/* <span className="text-[13px] tracking-wide text-blue-900/80 font-semibold">
              PAGOS EN LÍNEA
            </span> */}
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-6 w-full flex flex-col items-center"
            noValidate
          >
            <div className="w-full">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Número de NIT - Ejemplo: xxx.xxx.xxx-x"
                className={[
                  'w-full h-12 rounded-full',
                  'bg-white/95 border border-gray-300',
                  'px-5 text-gray-800 placeholder-gray-400',
                  'outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400',
                  errors.nit ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : '',
                ].join(' ')}
                {...register('nit', {
                  required: 'El NIT es obligatorio',
                  pattern: {
                    value: /^[0-9.\-]+$/,
                    message: 'El NIT solo puede contener números, puntos y guiones',
                  },
                  minLength: {
                    value: 6,
                    message: 'El NIT debe tener al menos 6 caracteres',
                  },
                })}
              />
              {errors.nit && (
                <p className="mt-2 text-sm text-red-600">{errors.nit.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={[
                "mt-4 h-12 px-12 rounded-full font-semibold uppercase tracking-[0.25em]",
                "transition-all duration-200 ease-out shadow-md cursor-pointer",
                loading
                  ? "border border-ec-primary-300 text-ec-primary-300 bg-white/80 cursor-wait animate-pulse"
                  : "border border-ec-primary-500 text-ec-primary-600 bg-white hover:bg-ec-primary-500 hover:text-white",
                "disabled:cursor-not-allowed disabled:opacity-70",
              ].join(" ")}
            >
              {loading ? "ENVIANDO…" : "ENVIAR"}
            </button>

            {error && (
              <div className="mt-5 w-full rounded-xl bg-red-50 border border-red-300 px-4 py-3 text-red-800 text-sm flex items-start gap-3 shadow-sm">
                <Icon name="alert-circle" className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}


          </form>

          <button
            onClick={handleDownloadInstructivo}
            disabled={downloading}
            className={[
              "mt-8 inline-flex items-center justify-center w-full sm:w-auto",
              "rounded-full px-10 h-14 font-semibold tracking-wide shadow-md",
              "bg-ec-primary-50 text-ec-primary-700 hover:bg-ec-primary-100 hover:scale-[1.02]",
              "transition-all duration-200 cursor-pointer",
              downloading ? "opacity-75 cursor-wait" : "",
            ].join(" ")}
          >
            <span className="flex items-center gap-3">
              <span className="flex items-center justify-center h-8 w-8">
                <Icon
                  name={downloading ? "download" : "file-text"}
                  className={`h-5 w-5 text-ec-primary-700 ${downloading ? "animate-bounce" : ""}`}
                />
              </span>
              {downloading ? "DESCARGANDO…" : "DESCARGA AQUÍ NUESTRO INSTRUCTIVO"}
            </span>
          </button>

        </div>

        <div className="text-center pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            ¿No tiene cuenta registrada?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="font-medium text-ec-primary-600 hover:text-ec-primary-500 transition-colors"
            >
              Registrarse aquí
            </button>
          </p>
        </div>

      </div>

      <div className="fixed bottom-6 right-6 flex items-center space-x-3 z-50">
        <a
          href="https://wa.me/+573150981918?text=Hola,%20tengo%20un%20problema%20con%20la%20página%20de%20pagos"
          target="_blank"
          rel="noreferrer noopener"
          className="hidden sm:inline-block text-sm font-medium text-blue-900/80 hover:text-blue-900 transition-colors"
        >
          ¿Necesitas ayuda?
        </a>

        <a
          href="https://wa.me/+573150981918?text=Hola,%20tengo%20un%20problema%20con%20la%20página%20de%20pagos"
          target="_blank"
          rel="noreferrer noopener"
          className="flex items-center justify-center h-14 w-14 rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 hover:scale-105 transition-transform duration-200"
          aria-label="WhatsApp"
        >
          <Icon legacyIcon="IconWhp.png" className="h-7 w-7" />
        </a>
      </div>

    </section>
  );
};

export default Login;
